import { get, post } from "../../common/fetch";
import {
  PageParam,
  SaleActivityUser,
  SaleExpert,
} from "./types";

const numberKeys = [
  "activityId",
  "saleActivityId",
  "activityDetailId",
  "saleActivityDetailId",
  "teachingActivityId",
  "liveActivityRoomId",
  "projectId",
  "topicId",
  "courseId",
  "sectionId",
  "age",
];

const toNumberValue = (value: any) => {
  if (value === "" || value === null || value === undefined) return value;
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? value : numberValue;
};

export const normalizeNumberIds = <T extends any>(params: T): T => {
  if (Array.isArray(params)) {
    return params.map((item) => normalizeNumberIds(item)) as T;
  }
  if (!params || typeof params !== "object") return params;
  const next: any = { ...(params as any) };
  Object.keys(next).forEach((key) => {
    if (numberKeys.includes(key)) {
      next[key] = toNumberValue(next[key]);
      return;
    }
    if (Array.isArray(next[key]) || (next[key] && typeof next[key] === "object")) {
      next[key] = normalizeNumberIds(next[key]);
    }
  });
  return next;
};

export const fetchSaleActivityList = (params: {
  activityInfo: any;
  page: PageParam;
}) => post("/shop/front/marketing/activity/list", normalizeNumberIds(params), false);

type NumberLike = string | number;

export const fetchSaleActivityInfo = (activityId: NumberLike) =>
  post(`/shop/front/marketing/activity/${activityId}/info`, {}, false);

export const fetchSaleActivityExpertInfo = () =>
  post("/shop/front/marketing/activity/expert/info", {}, false);

export const fetchSaleActivityUserInfo = (saleActivityId: NumberLike) =>
  post(
    "/shop/front/marketing/activity/user/info",
    normalizeNumberIds({ saleActivityId, activityId: saleActivityId }),
    false
  );

export const fetchInstituteList = (params: {
  instituteName?: string;
  provinceCode?: string;
  cityCode?: string;
  districtCode?: string;
} = {}) => post("/main/api/institute/list", params, false);

export const fetchRegionCascadeList = () =>
  post("/main/region/getRegionCascadeList", {}, false);

export const submitSaleActivityExpert = (params: SaleExpert) =>
  post("/shop/front/marketing/expert/submit", normalizeNumberIds(params), true);

export const fetchSaleActivityUserFieldConf = (saleActivityId: NumberLike) =>
  get(
    `/shop/front/sale/activity/user/signup/field/conf?saleActivityId=${encodeURIComponent(
      String(saleActivityId)
    )}`,
    null,
    false
  );

export const submitSaleActivityUser = (params: SaleActivityUser) =>
  post("/shop/front/sale/activity/save/user/signup/info", normalizeNumberIds(params), true);

export const fetchMySaleActivityList = (
  type: "expert" | "user",
  params: { activityInfo?: any; page: PageParam }
) => post(`/shop/front/marketing/my/${type}/activity/list`, normalizeNumberIds(params), false);

export const fetchMySaleActivityAuditInfo = (
  type: "expert" | "user",
  activityId: NumberLike
) =>
  post(
    `/shop/front/marketing/my/${type}/activity/${activityId}/audit/info`,
    {},
    false
  );

export const fetchMySaleActivityLearnInfo = (
  type: "expert" | "user",
  activityId: NumberLike
) =>
  post(
    `/shop/front/marketing/my/${type}/activity/${activityId}/info`,
    {},
    false
  );

export const fetchMySaleActivityTeachingActivityDetail = (
  type: "expert" | "user",
  activityDetailId: NumberLike
) =>
  post(
    `/shop/front/marketing/my/${type}/activity/${activityDetailId}/teaching/activity/detail`,
    {},
    false
  );

export const fetchSaleActivityLiveRoomList = (params: {
  saleActivityId?: NumberLike;
  saleActivityDetailId: NumberLike;
  currentOperatorType?: number;
}) =>
  post(
    "/shop/front/marketing/live/activity/room/list",
    normalizeNumberIds(params),
    false
  );

export const fetchSaleActivityExpertLiveRoomInfo = (params: {
  saleActivityId?: NumberLike;
  saleActivityDetailId: NumberLike;
  liveActivityRoomId: NumberLike;
  currentOperatorType?: number;
}) =>
  post(
    "/shop/front/marketing/live/activity/expert/room/info",
    normalizeNumberIds(params),
    true
  );

export const fetchSaleActivityUserEnterLiveRoomUrl = (params: {
  saleActivityId: NumberLike;
  saleActivityDetailId: NumberLike;
  liveActivityRoomId: NumberLike;
}) =>
  post(
    "/shop/front/marketing/live/activity/user/enter/room/url",
    normalizeNumberIds(params),
    true
  );

export const fetchTeachingActivityAttachmentList = (teachingActivityId: NumberLike) =>
  post(
    `/shop/front/ta/user/activity/${teachingActivityId}/attachment/list`,
    {},
    false
  );

export const fetchSaleActivityProjectInfo = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
}) => post("/shop/front/marketing/project/info", normalizeNumberIds(params), false);

export const fetchSaleActivityProjectCourseList = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
  page?: PageParam;
}) => post("/shop/front/marketing/project/course/list", normalizeNumberIds(params), false);

export const fetchSaleActivityProjectTopicList = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
  page?: PageParam;
}) => post("/shop/front/marketing/project/topic/list", normalizeNumberIds(params), false);

export const fetchSaleActivityProjectTopicInfo = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
  topicId: NumberLike;
}) => post("/shop/front/marketing/project/topic/info", normalizeNumberIds(params), false);

export const fetchSaleActivityProjectTopicCourseList = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
  topicId: NumberLike;
  page: PageParam;
}) => post("/shop/front/marketing/project/topic/course/list", normalizeNumberIds(params), false);

export const saveSaleActivityProjectReadNotice = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
}) => post("/shop/front/marketing/project/saveReadNotice", normalizeNumberIds(params), false);

export const getSaleActivityProjectQuestionnaireUrl = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
  courseId?: NumberLike;
  topicId?: NumberLike;
  sectionId?: NumberLike;
  videoId?: NumberLike;
}) => post("/shop/front/marketing/getProjectStarQuestionnaireUrl", normalizeNumberIds(params), true);

export const getSaleActivityCourseQuestionnaireUrl = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
  courseId: NumberLike;
}) =>
  post(
    "/shop/front/marketing/getProjectCourseStarQuestionnaireUrl",
    normalizeNumberIds(params),
    true
  );

export const getSaleActivityTopicCourseQuestionnaireUrl = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
  topicId: NumberLike;
  courseId: NumberLike;
}) =>
  post(
    "/shop/front/marketing/getProjectTopicCourseStarQuestionnaireUrl",
    normalizeNumberIds(params),
    true
  );

export const getSaleActivityTopicQuestionnaireUrl = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
  topicId: NumberLike;
}) =>
  post(
    "/shop/front/marketing/getProjectTopicStarQuestionnaireUrl",
    normalizeNumberIds(params),
    true
  );

export const checkSaleActivityProjectExam = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
  examManagerId?: string;
}) => post("/shop/front/marketing/exam/checkProjectExam", normalizeNumberIds(params), true);

export const getSaleActivityProjectExamPaperInfo = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
  examManagerId?: string;
}) => post("/shop/front/marketing/exam/getProjectExamPaperInfo", normalizeNumberIds(params), true);

export const checkSaleActivityTopicExam = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
  topicId: NumberLike;
}) => post("/shop/front/marketing/exam/checkProjectTopicExam", normalizeNumberIds(params), true);

export const getSaleActivityTopicExamPaperInfo = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
  topicId: NumberLike;
}) => post("/shop/front/marketing/exam/getProjectTopicExamPaperInfo", normalizeNumberIds(params), true);

export const checkSaleActivityCourseExam = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
  courseId: NumberLike;
}) => post("/shop/front/marketing/exam/checkProjectCourseExam", normalizeNumberIds(params), true);

export const getSaleActivityCourseExamPaperInfo = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
  courseId: NumberLike;
}) =>
  post(
    "/shop/front/marketing/exam/getProjectCourseExamPaperInfo",
    normalizeNumberIds(params),
    true
  );

export const checkSaleActivityTopicCourseExam = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
  topicId: NumberLike;
  courseId: NumberLike;
}) =>
  post(
    "/shop/front/marketing/exam/checkProjectTopicCourseExam",
    normalizeNumberIds(params),
    true
  );

export const getSaleActivityTopicCourseExamPaperInfo = (params: {
  saleActivityId: NumberLike;
  projectId: NumberLike;
  topicId: NumberLike;
  courseId: NumberLike;
}) =>
  post(
    "/shop/front/marketing/exam/getProjectTopicCourseExamPaperInfo",
    normalizeNumberIds(params),
    true
  );
